import { useQuery } from '@tanstack/react-query';
import { logsApi } from '@/api/logs';
import { useTimeRangeStore } from '@/stores/timeRangeStore';
import { isWithinLiveTailWindow } from '@/utils/liveTail';
import type { LogFilters } from '../types/index';
import ButtonGroup from './ButtonGroup';
import DateTimePicker from './DateTimePicker';

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
    filters: LogFilters;
    onFiltersChange: (filters: LogFilters) => void;
}

function Sidebar({ isOpen, onClose, filters, onFiltersChange }: SidebarProps) {
    const { followNow, setFollowNow } = useTimeRangeStore();

    const {
        data: availableProjects = [],
        error: projectsError,
        isLoading: isProjectsLoading
    } = useQuery({
        queryKey: ['log-projects'],
        queryFn: ({ signal }) => logsApi.getProjects(signal),
        staleTime: 60_000,
        refetchInterval: 60_000,
        retry: 1
    });

    const formatProjectLabel = (project: string) => {
        const knownLabels: Record<string, string> = {
            project314: 'Project314',
            watchmode: 'Watchmode',
            'watchmode-api': 'Watchmode API',
            'watchmode-backend': 'Watchmode Backend'
        };

        return knownLabels[project] ?? project;
    };

    const projectOptions: Array<{ value: string; label: string }> = availableProjects.map(({ project }) => ({
        value: project,
        label: formatProjectLabel(project)
    }));

    const handleFilterChange = (key: keyof LogFilters, value: string | string[] | number | number[]) => {
        onFiltersChange({ ...filters, [key]: value });
    };

    const handleProjectChange = (values: string[]) => {
        const nextFilters = { ...filters };

        if (values.length === 0 || (projectOptions.length > 0 && values.length === projectOptions.length)) {
            delete nextFilters.project;
            onFiltersChange(nextFilters);
            return;
        }

        nextFilters.project = values;
        onFiltersChange(nextFilters);
    };

    const clearFilters = () => {
        setFollowNow(true);
        onFiltersChange({});
    };

    const handleDateRangeChange = (start: string, end: string) => {
        const endChanged = filters.dateTo !== end;

        if (endChanged) {
            if (isWithinLiveTailWindow(end)) {
                setFollowNow(true);
            } else {
                setFollowNow(false);
            }
        }

        onFiltersChange({
            ...filters,
            dateFrom: start,
            dateTo: end
        });
    };

    const toggleFollowNow = (nextFollowNow: boolean) => {
        setFollowNow(nextFollowNow);

        if (!nextFollowNow && isWithinLiveTailWindow(filters.dateTo)) {
            onFiltersChange({
                ...filters,
                dateTo: new Date().toISOString()
            });
        }
    };

    // Log level options - matching Logger interface
    const logLevelOptions = [
        { value: 'debug', label: 'Debug' },
        { value: 'info', label: 'Info' },
        { value: 'warn', label: 'Warning' },
        { value: 'error', label: 'Error' },
        { value: 'job', label: 'Job' }
    ];

    // Severity level options (0-10 range matching Logger interface)
    const severityOptions = [
        { value: 0, label: '0' },
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
        { value: 7, label: '7' },
        { value: 8, label: '8' },
        { value: 9, label: '9' },
        { value: 10, label: '10' }
    ];

    // Helper functions to ensure arrays for ButtonGroup
    const getProjectArray = (): string[] => {
        if (!filters.project) {
            return projectOptions.map((option) => option.value);
        }

        const selectedProjects = Array.isArray(filters.project) ? filters.project : [filters.project];

        if (projectOptions.length === 0) {
            return selectedProjects;
        }

        return selectedProjects.filter((project) => projectOptions.some((option) => option.value === project));
    };

    const getLevelArray = (): string[] => {
        if (!filters.level) return [];
        return Array.isArray(filters.level) ? filters.level : [filters.level];
    };

    const getSeverityArray = (): number[] => {
        if (!filters.severityThreshold) return [];
        return Array.isArray(filters.severityThreshold) ? filters.severityThreshold : [filters.severityThreshold];
    };

    return (
        <>
            {/* Backdrop */}
            {isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose}></div>}

            {/* Sidebar */}
            <aside
                className={`
                    fixed lg:static inset-y-0 left-0 z-50 w-80 bg-dark transform transition-transform duration-300 ease-in-out
                    ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                    border-r border-zinc-700
                `}
            >
                <div className="flex flex-col h-full">
                    {/* Sidebar Header */}
                    <div className="flex items-center justify-between p-4 border-b border-zinc-700">
                        <h2 className="text-lg font-semibold text-white">Filters</h2>
                        <button onClick={onClose} className="lg:hidden text-zinc-400 hover:text-white" aria-label="Close sidebar">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    {/* Filter Form */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {/* Search */}
                        <div>
                            <label className="form-label">
                                <i className="fas fa-search mr-2"></i>
                                Search Logs
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search in log messages..."
                                value={filters.search || ''}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                            />
                        </div>

                        {/* Date Range - Using DateTimePicker */}
                        <div className="border-b border-zinc-700 pb-4">
                            <h3 className="text-sm font-medium text-zinc-300 mb-3">
                                <i className="fas fa-calendar mr-2"></i>
                                Date Range
                            </h3>
                            <label className="flex items-center gap-2 text-xs text-zinc-300 mb-3 select-none">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 accent-blue-500"
                                    checked={followNow}
                                    onChange={(e) => toggleFollowNow(e.target.checked)}
                                />
                                Live tail (ignore end time)
                            </label>
                            {followNow && <div className="text-xs text-zinc-500 mb-3">End time is ignored while Live tail is enabled.</div>}
                            {!followNow && (
                                <div className="text-xs text-amber-400 mb-3">
                                    Historical mode is active. New rows will not appear until live tail is turned back on.
                                </div>
                            )}
                            <DateTimePicker
                                startDateTime={filters.dateFrom || ''}
                                endDateTime={filters.dateTo || ''}
                                onDateRangeChange={handleDateRangeChange}
                            />
                        </div>

                        {/* Project - Multi-select ButtonGroup */}
                        <ButtonGroup
                            label="Projects"
                            icon="fas fa-project-diagram"
                            options={projectOptions}
                            selectedValues={getProjectArray()}
                            onChange={(values) => handleProjectChange(values as string[])}
                            disabled={isProjectsLoading || projectOptions.length === 0}
                            clearSelectionLabel="Clear Filter"
                        />
                        {isProjectsLoading && <div className="text-xs text-zinc-500 -mt-2">Loading available projects...</div>}
                        {projectsError && <div className="text-xs text-red-400 -mt-2">Unable to load project options.</div>}

                        {/* Log Level - Multi-select ButtonGroup */}
                        <ButtonGroup
                            label="Log Levels"
                            icon="fas fa-layer-group"
                            options={logLevelOptions}
                            selectedValues={getLevelArray()}
                            onChange={(values) => handleFilterChange('level', values as string[])}
                        />

                        {/* Severity Threshold - Multi-select ButtonGroup */}
                        <ButtonGroup
                            label="Severity Levels"
                            icon="fas fa-exclamation-triangle"
                            options={severityOptions}
                            selectedValues={getSeverityArray()}
                            onChange={(values) => handleFilterChange('severityThreshold', values as number[])}
                        />

                        {/* Service */}
                        <div>
                            <label className="form-label">
                                <i className="fas fa-cogs mr-2"></i>
                                Service
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g., auth-service, database-service"
                                value={filters.service || ''}
                                onChange={(e) => handleFilterChange('service', e.target.value)}
                            />
                        </div>

                        {/* Category */}
                        <div>
                            <label className="form-label">
                                <i className="fas fa-tags mr-2"></i>
                                Category
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g., authentication, connection, api"
                                value={filters.category || ''}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                            />
                        </div>

                        {/* Environment */}
                        <div>
                            <label className="form-label">
                                <i className="fas fa-globe mr-2"></i>
                                Environment
                            </label>
                            <select
                                className="form-control"
                                value={filters.env || ''}
                                onChange={(e) => handleFilterChange('env', e.target.value)}
                            >
                                <option value="">All Environments</option>
                                <option value="development">Development</option>
                                <option value="staging">Staging</option>
                                <option value="production">Production</option>
                                <option value="test">Test</option>
                            </select>
                        </div>

                        {/* Host */}
                        <div>
                            <label className="form-label">
                                <i className="fas fa-server mr-2"></i>
                                Host
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g., app-server-01, db-server-02"
                                value={filters.host || ''}
                                onChange={(e) => handleFilterChange('host', e.target.value)}
                            />
                        </div>

                        {/* Tags */}
                        <div>
                            <label className="form-label">
                                <i className="fas fa-hashtag mr-2"></i>
                                Tags
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter tags separated by commas"
                                value={filters.tags?.join(', ') || ''}
                                onChange={(e) => {
                                    const tags = e.target.value
                                        .split(',')
                                        .map((tag) => tag.trim())
                                        .filter((tag) => tag.length > 0);
                                    handleFilterChange('tags', tags);
                                }}
                            />
                            <small className="text-zinc-500 text-xs mt-1">Example: database, timeout, critical</small>
                        </div>

                        {/* Actions */}
                        <div className="border-t border-zinc-700 pt-4">
                            <div className="flex space-x-2">
                                <button onClick={clearFilters} className="btn btn-secondary flex-1" title="Clear all filters">
                                    <i className="fas fa-eraser mr-2"></i>
                                    Clear All
                                </button>
                            </div>

                            {/* Filter Summary */}
                            <div className="mt-3 text-xs text-zinc-500">
                                {
                                    Object.keys(filters).filter((key) => {
                                        const value = filters[key as keyof LogFilters];
                                        return value && (Array.isArray(value) ? value.length > 0 : value.toString().trim() !== '');
                                    }).length
                                }{' '}
                                filter(s) active
                            </div>
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
}

export default Sidebar;
